import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { grafanaApiService } from '../../../../services/grafanaApiService';
import { tokenRefreshService } from '../../../../utils/tokenRefreshService';
import { tokenManager } from '../../../../utils/tokenManager';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { RefreshCw } from 'lucide-react';
import styles from './GrafanaDashboard.module.css';


export const GrafanaDashboard = ({ serviceKey }) => {
  const [dashboardUrl, setDashboardUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);  // Initial bundle loading
  const [isDataLoading, setIsDataLoading] = useState(false);  // After onLoad, data fetching
  const [refreshCountdown, setRefreshCountdown] = useState(null);
  const { instance, accounts, inProgress } = useMsal();
  const unregisterCallback = useRef(null);

  // MSAL instance is required for tokenManager to issue silent refreshes.
  useEffect(() => {
    if (instance && accounts) {
      tokenManager.initialize(instance, accounts);
    }
  }, [instance, accounts]);

  // Fetches a signed Grafana iframe URL
  //Used on inital load and dashboard changes
  const loadDashboardUrl = useCallback(async (isTokenRefresh = false) => {
    try {
      if (isTokenRefresh) {
        setRefreshCountdown(3);
        const countdownInterval = setInterval(() => {
          setRefreshCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              return null;
            }
            return prev - 1;
          });
        }, 1000);
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      const currentToken = await tokenManager.getToken();
      if (!currentToken) {
        setError('User is not authenticated');
        return;
      }

      const url = await grafanaApiService.getDashboardUrl(serviceKey, currentToken);
      setDashboardUrl(url);
      setError(null);
    } catch (error) {
      console.error('Failed to load dashboard URL:', error);
      setError(error.message || 'Failed to load dashboard');
      setIsLoading(false);
    } finally {
      setRefreshCountdown(null);
    }
  }, [serviceKey]);

  // Handle token refresh callback
  const handleTokenRefresh = useCallback(async (newToken) => {
    try {
      tokenManager.updateToken(newToken);
      grafanaApiService.clearCache();
      const url = await grafanaApiService.getDashboardUrl(serviceKey, newToken);
      setDashboardUrl(url);
    } catch (error) {
      console.error('Failed to update URL with new token:', error);
    }
  }, [serviceKey]);

  // When iframe loads, only hide the first loader
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Monitors for Grafana's API calls to complete
  useEffect(() => {
    if (!isLoading && dashboardUrl) {
      setIsDataLoading(true);

      // Monitor Performance API for Grafana's query completion
      let checkCount = 0;
      const maxChecks = 30;

      const checkGrafanaReady = setInterval(() => {
        checkCount++;

        try {
          // Gets same-origin resource entries from Performance API
          const resourceEntries = (typeof performance !== 'undefined' &&
            typeof performance.getEntriesByType === 'function')
            ? performance.getEntriesByType('resource')
            : [];
          const entries = resourceEntries.filter(entry => {
            try {
              const url = typeof entry.name === 'string'
                ? new URL(entry.name, window.location.href)
                : null;
              return url !== null && url.origin === window.location.origin;
            } catch (e) {
              return false;
            }
          });

          // Looks for Grafana's critical API calls that signal dashboard data loading
          const hasQueryAPI = entries.some(entry =>
            entry.name.includes('/api/ds/query') ||
            entry.name.includes('/api/datasources') ||
            entry.name.includes('grafana-postgresql-datasource') ||
            entry.name.includes('module.js')
          );

          // If API calls are found OR reached max checks, prepare to hide loader
          if (hasQueryAPI || checkCount >= maxChecks) {
            clearInterval(checkGrafanaReady);
            setTimeout(() => {
              setIsDataLoading(false);
            }, 150);
          }
        } catch (error) {
          // If Performance API is restricted, falls back to timer
          console.log('Performance monitoring not available');
          clearInterval(checkGrafanaReady);
          setTimeout(() => {
            setIsDataLoading(false);
          }, 300);
        }
      }, 200);

      // fallback to force hide after 10 seconds
      const fallbackTimeout = setTimeout(() => {
        clearInterval(checkGrafanaReady);
        setIsDataLoading(false);
      }, 10000);

      return () => {
        clearInterval(checkGrafanaReady);
        clearTimeout(fallbackTimeout);
      };
    }
  }, [isLoading, dashboardUrl]);

  // Loads dashboard URL when serviceKey changes
  useEffect(() => {
    if (serviceKey && inProgress === InteractionStatus.None) {
      setDashboardUrl(null);
      setError(null);
      setIsLoading(true);  // Shows first loader when switching dashboards
      setIsDataLoading(false);  // Resets second loader
      loadDashboardUrl(false);
    }
  }, [serviceKey, inProgress, loadDashboardUrl]);

  // Registers for token refresh notifications
  useEffect(() => {
    unregisterCallback.current = tokenRefreshService.registerCallback(handleTokenRefresh);
    return () => {
      if (unregisterCallback.current) {
        unregisterCallback.current();
      }
    };
  }, [handleTokenRefresh]);

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h3>Failed to load dashboard</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Shows loading while Grafana URLs are being fetched
  if (!dashboardUrl) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Preparing dashboard...</p>
        </div>
      </div>
    );
  }

  // Renders iframe with loading overlays
  return (
    <div className={styles.container}>
      {refreshCountdown !== null && (
        <div className={styles.refreshNotification}>
          <RefreshCw className={styles.refreshIcon} size={20} />
          <span>Refreshing dashboard in {refreshCountdown}s...</span>
        </div>
      )}

      <div className={styles.dashboardContainer}>
        <iframe
          key={dashboardUrl}
          src={dashboardUrl}
          width="100%"
          height="800px"
          style={{
            border: 'none',
            borderRadius: '8px',
            backgroundColor: 'rgb(17 18 23)'
          }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-downloads"
          loading="eager"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Grafana Dashboard"
          aria-label="Dashboard visualization"
          onLoad={handleIframeLoad}
        />

        {/* Stage 1: Initial loading overlay (while bundles download) */}
        {isLoading && (
          <div className={styles.initialLoadingOverlay}>
            <div className={styles.loadingContent}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Loading Dashboard...</p>
              <p className={styles.loaderSubtext}>
                Please wait while we load your analytics
              </p>
            </div>
          </div>
        )}

        {/* Stage 2: Data loading overlay (after onLoad, while Grafana fetches data) */}
        {isDataLoading && (
          <div className={styles.dataLoadingOverlay}>
            <div className={styles.loadingContent}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>
                Fetching dashboard data...
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

GrafanaDashboard.propTypes = {
  serviceKey: PropTypes.string.isRequired,
};